import type { IPageManager } from '@core/application/PageManager'
import { PageManager } from '@core/application/PageManager'
import { Env } from '@core/EnvUtils'
import { getMainFrameBaseUrl } from '@core/application/AppUtils'
import { AppCategory, MicroAppId, SystemAppId } from '@core/application/ApplicationConstants'
import { TrackAppId } from '@core/TrackConstants'
import { getMappEntryUrlByAppId } from '@/BuildConfig'
// 微应用icon标识
import iconFile from '@assets/svgs/preload-icon-file.png'
import iconFileLocked from '@assets/svgs/preload-icon-file-locked.png'
import iconNote from '@assets/svgs/icon-note.png'
import iconNoteLocked from '@assets/svgs/icon-note-locked.png'
import iconTransferDock from '@assets/svgs/icon-transfer-dock.png'
import iconTransferDockLocked from '@assets/svgs/icon-transfer-dock-locked.png'
import iconCloudDocument from '@assets/svgs/icon-cloud-document.png'
import iconCloudDocumentLocked from '@assets/svgs/icon-cloud-document-locked.png'
import { getMappBlackList } from '@/DebugConfig'
import { v4 as uuidV4 } from 'uuid'
import { i18n } from '@/i18n'
import { isInIosAPP } from '../Utils'

const { t } = i18n.global
// 系统保留APP entry
const ENTRY_URL_DEV_INTEGRATION = 'https://localhost:8080'
const ENTRY_URL_LOCAL_TEST = 'https://localhost:8020'

// 微应用固定入口标识
export const MicroAppEntry = {
  INDEX: 'index.html',
  NAVIGATOR: 'navigator.html',
}

export interface BaseAppInfo {
  name: string
  entry: string
  icon?: string
  isCloudService?: boolean
  trackId?: TrackAppId
  needClientValidation: boolean
  internationalizationKey?: string
  iconLocked?: string
}

export interface AppInfo extends BaseAppInfo {
  id: string
  category: AppCategory
  hasSubNavigation: boolean
}

class AppInfoImpl implements AppInfo {
  entry: string
  icon?: string | undefined
  isCloudService?: boolean | undefined
  trackId?: TrackAppId | undefined
  needClientValidation: boolean
  id: string
  category: AppCategory
  hasSubNavigation: boolean
  internationalizationKey?: string
  iconLocked?: string | undefined
  constructor(info: Omit<AppInfo, 'name'>) {
    const {
      entry,
      icon,
      isCloudService,
      trackId,
      needClientValidation,
      id,
      category,
      hasSubNavigation,
      internationalizationKey,
      iconLocked,
    } = info

    this.entry = entry
    this.icon = icon
    this.isCloudService = isCloudService
    this.trackId = trackId
    this.needClientValidation = needClientValidation
    this.id = id
    this.category = category
    this.hasSubNavigation = hasSubNavigation
    this.internationalizationKey = internationalizationKey
    this.iconLocked = iconLocked
  }

  get name() {
    return this.internationalizationKey ? t(this.internationalizationKey) : ''
  }
}

interface ICategoryInfo {
  name: string
  icon?: string
}
// 下面的名称暂时没有使用，如果需要使用，也需要注意国际化的实时变化
const AppCategoryMap = new Map<AppCategory, ICategoryInfo>([
  [AppCategory.FILE, { name: t('owork_base.application_name.file'), icon: iconFile }],
  [AppCategory.NOTE, { name: t('owork_base.application_name.note'), icon: iconNote }],
  [AppCategory.TEST, { name: '测试', icon: iconFile }],
  [AppCategory.DOCK, { name: t('owork_base.application_name.dock'), icon: iconTransferDock }],
])

export const PLACEHOLDER_APPLICATION_NAME = '__placeholder_application'

const getPlaceholderApplicationId = (category: AppCategory) =>
  Array.of(PLACEHOLDER_APPLICATION_NAME, category.toString(), uuidV4()).join('_')

const getPlaceholderApplication = (category: AppCategory) => ({
  category,
  name: PLACEHOLDER_APPLICATION_NAME,
  id: getPlaceholderApplicationId(category),
  entry: '',
  hasSubNavigation: false,
  needClientValidation: false,
})

export const APP_LIST: AppInfo[] = [
  {
    category: AppCategory.FILE,
    internationalizationKey: 'owork_base.application_name.file_manager',
    id: MicroAppId.FILE_MANAGER,
    entry: getMappEntryUrlByAppId(MicroAppId.FILE_MANAGER),
    hasSubNavigation: true,
    icon: iconFile,
    trackId: TrackAppId.FILE_MANAGER,
    needClientValidation: false,
    iconLocked: iconFileLocked,
  },
  getPlaceholderApplication(AppCategory.FILE),
  {
    category: AppCategory.SYSTEM,
    internationalizationKey: 'owork_base.application_name.system',
    id: SystemAppId.SYSTEM,
    entry: '',
    hasSubNavigation: false,
    needClientValidation: false,
  },
  {
    category: AppCategory.SYSTEM,
    internationalizationKey: 'owork_base.application_name.transmission_center',
    id: SystemAppId.TRANSMISSION_CENTER,
    entry: '',
    hasSubNavigation: false,
    needClientValidation: false,
  },
  {
    category: AppCategory.FILE,
    internationalizationKey: 'owork_base.application_name.kingsoft_document',
    id: MicroAppId.KINGSOFT_DOCUMENT,
    entry: getMappEntryUrlByAppId(MicroAppId.KINGSOFT_DOCUMENT),
    hasSubNavigation: false,
    needClientValidation: false,
  },
  {
    category: AppCategory.NOTE,
    internationalizationKey: 'owork_base.application_name.sticky_notes',
    id: MicroAppId.STICKY_NOTES,
    entry: getMappEntryUrlByAppId(MicroAppId.STICKY_NOTES),
    hasSubNavigation: true,
    icon: iconNote,
    trackId: TrackAppId.STICKY_NOTES,
    needClientValidation: true,
    iconLocked: iconNoteLocked,
  },
  getPlaceholderApplication(AppCategory.NOTE),
  {
    category: AppCategory.FILE,
    internationalizationKey: 'owork_base.application_name.qq_document',
    id: MicroAppId.QQ_DOCUMENT,
    entry: getMappEntryUrlByAppId(MicroAppId.QQ_DOCUMENT),
    hasSubNavigation: false,
    needClientValidation: false,
  },
  {
    category: AppCategory.DOCK,
    internationalizationKey: 'owork_base.application_name.transfer_dock',
    id: MicroAppId.TRANSFER_DOCK,
    entry: getMappEntryUrlByAppId(MicroAppId.TRANSFER_DOCK),
    hasSubNavigation: false,
    icon: iconTransferDock,
    trackId: TrackAppId.TRANSFER_DOCK,
    needClientValidation: true,
    iconLocked: iconTransferDockLocked,
  },
].map((info) => new AppInfoImpl(info))

if (Env.isTest()) {
  APP_LIST.push(
    {
      category: AppCategory.TEST,
      name: '微应用开发联调用',
      id: localStorage.getItem('ENTRY_ID_DEV_INTEGRATION') ?? SystemAppId.DEV_INTEGRATION,
      entry: localStorage.getItem('ENTRY_URL_DEV_INTEGRATION') ?? ENTRY_URL_DEV_INTEGRATION,
      hasSubNavigation: true,
      icon: iconFile,
      needClientValidation: false,
    },
    getPlaceholderApplication(AppCategory.TEST),
    {
      category: AppCategory.DOCUMENT,
      name: '文档',
      internationalizationKey: 'owork_base.application_name.cloud_document',
      id: MicroAppId.CLOUD_DOCUMENT,
      entry: getMappEntryUrlByAppId(MicroAppId.CLOUD_DOCUMENT),
      hasSubNavigation: false,
      icon: iconCloudDocument,
      trackId: TrackAppId.CLOUD_DOCUMENT,
      needClientValidation: true,
      iconLocked: iconCloudDocumentLocked,
    }
  )
  // APP_LIST.push({
  //   category: AppCategory.DEMO,
  //   name: '通知中心测试用Demo',
  //   id: SystemAppId.NC_DEMO,
  //   entry: 'https://owork-mappdemo-cn-test.wanyol.com',
  //   hasSubNavigation: false,
  //   icon: iconFile,
  //   needClientValidation: false,
  // })
}

if (Env.isLocalDev()) {
  // 本地开发环境下，默认添加本地应用的信息
  APP_LIST.push({
    category: AppCategory.TEST,
    name: '本地测试',
    id: SystemAppId.LOCAL_DEV,
    entry: ENTRY_URL_LOCAL_TEST,
    hasSubNavigation: false,
    icon: iconFile,
    needClientValidation: false,
  })
}

export interface MicroAppRegister {
  (apps: AppInfo[], base: string): void
}

class ApplicationManager {
  readonly #microApps: Map<string, AppInfo> = new Map()

  constructor(apps: AppInfo[]) {
    for (const app of apps) {
      this.#microApps.set(app.id, app)
    }
  }

  /**
   * register all micro apps
   */
  registerMicroApps(register: MicroAppRegister): void {
    register(Array.from(this.#microApps.values()), getMainFrameBaseUrl())
  }

  /**
   * Return page manager for the given micro app.
   *
   * @param appId the id of the registered micro app
   */
  getPageManager(appId: string): IPageManager | undefined {
    const app = this.#microApps.get(appId)

    if (app) {
      return new PageManager(app.id)
    }
  }

  /**
   * return all micro apps
   *
   * @return AppInfo[]
   */
  getAppList(): AppInfo[] {
    return Array.from(this.#microApps.values())
  }

  /**
   * Return categoryName
   *
   * @param AppCategory 对应的AppCategory值
   *
   * @return  categoryName
   */
  getCategoryNameByType(AppCategory: AppCategory): string | undefined {
    return AppCategoryMap.get(AppCategory)?.name
  }

  /**
   * Return iconPath
   *
   * @param AppCategory 对应的AppCategory值
   *
   * @return  iconPath
   */
  getIconByCategory(AppCategory: AppCategory): string | undefined {
    return AppCategoryMap.get(AppCategory)?.icon
  }

  /**
   * Return iconPath
   *
   * @param appId the id of the registered micro app
   *
   * @return  iconPath
   */
  getAppIconById(appId: string): string | undefined {
    return this.#microApps.get(appId)?.icon
  }

  /**
   * Get registered micro app's name according to the given id
   *
   * @param appId the id of micro app
   */
  getAppName(appId: string): string | undefined {
    return this.#microApps.get(appId)?.name
  }

  /**
   * To determine if there is according to the given id
   *
   * @param appId the id of micro app
   * @returns true, if the given appId existed, otherwise false
   */
  hasApp(appId: string): boolean {
    return this.#microApps.has(appId)
  }

  /**
   * return default microApp id
   *
   * @return string | undefined
   */
  getDefaultMicroAppId(): string | undefined {
    if (this.#microApps.has(MicroAppId.FILE_MANAGER)) {
      return MicroAppId.FILE_MANAGER
    }

    for (const app of this.#microApps.values()) {
      if (app.category !== AppCategory.SYSTEM) {
        return app.id
      }
    }

    return undefined
  }

  isPlaceHolderApplication(appId: string) {
    return appId.startsWith(PLACEHOLDER_APPLICATION_NAME)
  }
}

function filterAppList(apps: AppInfo[]) {
  const blackList = getMappBlackList()
  let newAppList: AppInfo[] = apps

  if (blackList.length > 0) {
    newAppList = newAppList.filter(({ id }) => !blackList.includes(id))
  }

  if (isInIosAPP()) {
    newAppList = newAppList.filter(({ id }) => id !== MicroAppId.KINGSOFT_DOCUMENT && id !== MicroAppId.QQ_DOCUMENT)
  }

  return newAppList
}

const INSTANCE = new ApplicationManager(filterAppList(APP_LIST))

export { INSTANCE }
